/**
 * Notification Type Registry
 *
 * Single source of truth for all per-type notification configuration.
 * Merges data previously spread across notificationTypes.ts, notificationRoutes.ts,
 * and notification.types.ts into one `as const satisfies` object.
 *
 * To add a new PERSONAL (single-user) type, add an entry with `personal: true`,
 * icon, colors and priority — no pageId or route needed (see docs/NOTIFICATIONS.md).
 *
 * To add a new notification type:
 * 1. Add an entry to NOTIFICATION_TYPE_REGISTRY below
 * 2. The NotificationType union, routing, and display config update automatically
 */

import type { NotificationPageId } from './notificationPageRegistry';

/** Shape of each entry in the notification type registry (used for satisfies validation) */
interface NotificationTypeRegistryEntry {
  /** Which page this notification type belongs to. Required for broadcast types;
   *  omitted for personal types (`personal: true`), which have no settings page. */
  pageId?: NotificationPageId;
  /** Personal (single-recipient) type. Delivered via the per-user
   *  `userNotifications` collection; skips notification settings, page access,
   *  location and targetRole checks; navigates to the doc's own `url`.
   *  Needs only icon + colors + priority (route is optional). */
  personal?: boolean;
  /** FontAwesome icon class (e.g., 'fa-wrench') */
  icon: string;
  /** Toast background color (Tailwind class) */
  toastBg: string;
  /** Toast hover background — a complete `hover:bg-*` class.
   *  Must be the full class: Tailwind cannot see one assembled at runtime. */
  toastHoverBg: string;
  /** List icon background color (Tailwind class) */
  listIconBg: string;
  /** List icon text color (Tailwind class) */
  listIconText: string;
  /** Priority level */
  priority: 'low' | 'normal' | 'high' | 'urgent';
  /** Route path for click navigation (broadcast types; personal types use the doc's `url`) */
  route?: string;
  /** Query param name for the item ID (defaults to 'highlight') */
  itemParam?: string;
  /** Additional query params to add on navigation */
  additionalParams?: Record<string, string>;
}

/**
 * Central registry of all notification types.
 *
 * Adding a key here automatically:
 * - Adds it to the `NotificationType` union
 * - Provides display config (icon, colors, priority)
 * - Provides routing config (route, params)
 * - Maps it to a page ID
 */
export const NOTIFICATION_TYPE_REGISTRY = {
  // ===== Rental Board / Yard Types =====
  prep_request: {
    pageId: 'yard',
    icon: 'fa-wrench',
    toastBg: 'bg-orange-600',
    toastHoverBg: 'hover:bg-orange-700',
    listIconBg: 'bg-hue-yellow-soft',
    listIconText: 'text-hue-yellow-deep',
    priority: 'normal',
    route: '/rental/yard',
  },
  prep_done: {
    pageId: 'rentalBoard',
    icon: 'fa-check',
    toastBg: 'bg-green-600',
    toastHoverBg: 'hover:bg-green-700',
    listIconBg: 'bg-hue-green-soft',
    listIconText: 'text-hue-green-deep',
    priority: 'normal',
    route: '/rental/rentals',
  },
  return: {
    pageId: 'rentalBoard',
    icon: 'fa-flag-checkered',
    toastBg: 'bg-purple-600',
    toastHoverBg: 'hover:bg-purple-700',
    listIconBg: 'bg-hue-purple-soft',
    listIconText: 'text-hue-purple-deep',
    priority: 'normal',
    route: '/rental/rentals',
  },
  swap: {
    pageId: 'rentalBoard',
    icon: 'fa-right-left',
    toastBg: 'bg-green-600',
    toastHoverBg: 'hover:bg-green-700',
    listIconBg: 'bg-hue-green-soft',
    listIconText: 'text-hue-green-deep',
    priority: 'normal',
    route: '/rental/rentals',
  },
  unlink: {
    pageId: 'rentalBoard',
    icon: 'fa-link-slash',
    toastBg: 'bg-orange-600',
    toastHoverBg: 'hover:bg-orange-700',
    listIconBg: 'bg-hue-red-soft',
    listIconText: 'text-hue-red-deep',
    priority: 'low',
    route: '/rental/rentals',
  },
  cross_location_return_source: {
    pageId: 'rentalBoard',
    icon: 'fa-triangle-exclamation',
    toastBg: 'bg-red-600',
    toastHoverBg: 'hover:bg-red-700',
    listIconBg: 'bg-hue-red-soft',
    listIconText: 'text-hue-red-deep',
    priority: 'urgent',
    route: '/rental/rentals',
  },
  cross_location_return_destination: {
    pageId: 'rentalBoard',
    icon: 'fa-triangle-exclamation',
    toastBg: 'bg-red-600',
    toastHoverBg: 'hover:bg-red-700',
    listIconBg: 'bg-hue-red-soft',
    listIconText: 'text-hue-red-deep',
    priority: 'urgent',
    route: '/rental/rentals',
    additionalParams: { modal: 'cross-location' },
  },

  // ===== Trucking Calendar Types =====
  trucking_reminder: {
    pageId: 'truckingCalendar',
    icon: 'fa-truck',
    toastBg: 'bg-emerald-600',
    toastHoverBg: 'hover:bg-emerald-700',
    listIconBg: 'bg-hue-emerald-soft',
    listIconText: 'text-hue-emerald-deep',
    priority: 'normal',
    route: '/rental/trucking-calendar',
    itemParam: 'highlight',
  },
  trucking_event_scheduled: {
    pageId: 'truckingCalendar',
    icon: 'fa-calendar-plus',
    toastBg: 'bg-teal-600',
    toastHoverBg: 'hover:bg-teal-700',
    listIconBg: 'bg-hue-teal-soft',
    listIconText: 'text-hue-teal-deep',
    priority: 'normal',
    route: '/rental/trucking-calendar',
    itemParam: 'highlight',
  },
  trucking_status_change: {
    pageId: 'truckingCalendar',
    icon: 'fa-truck-moving',
    toastBg: 'bg-sky-600',
    toastHoverBg: 'hover:bg-sky-700',
    listIconBg: 'bg-hue-sky-soft',
    listIconText: 'text-hue-sky-deep',
    priority: 'normal',
    route: '/rental/trucking-calendar',
    itemParam: 'highlight',
  },

  // ===== Service Ticket Types =====

  // ===== Personal (single-user) Types =====
  quiz_assigned: {
    personal: true,
    icon: 'fa-clipboard-question',
    toastBg: 'bg-primary',
    toastHoverBg: 'hover:bg-primary-hover',
    listIconBg: 'bg-hue-blue-soft',
    listIconText: 'text-hue-blue-deep',
    priority: 'normal',
  },
  quiz_due_soon: {
    personal: true,
    icon: 'fa-clock',
    toastBg: 'bg-warning',
    toastHoverBg: 'hover:bg-warning-deep',
    listIconBg: 'bg-hue-amber-soft',
    listIconText: 'text-hue-amber-deep',
    priority: 'high',
  },
  // Quiz scenarios (webapp/services/quiz_scenarios_service.py). url is /quiz/step/{stepId}
  // for the step types and /{dept}/quizzes/runs/{scenarioId} for review_ready (to the run's reviewer).
  scenario_step_ready: {
    personal: true,
    icon: 'fa-people-arrows',
    toastBg: 'bg-primary',
    toastHoverBg: 'hover:bg-primary-hover',
    listIconBg: 'bg-hue-indigo-soft',
    listIconText: 'text-hue-indigo-deep',
    priority: 'high',
  },
  scenario_step_redo: {
    personal: true,
    icon: 'fa-rotate-left',
    toastBg: 'bg-warning',
    toastHoverBg: 'hover:bg-warning-deep',
    listIconBg: 'bg-hue-amber-soft',
    listIconText: 'text-hue-amber-deep',
    priority: 'high',
  },
  scenario_review_ready: {
    personal: true,
    icon: 'fa-clipboard-check',
    toastBg: 'bg-primary',
    toastHoverBg: 'hover:bg-primary-hover',
    listIconBg: 'bg-hue-indigo-soft',
    listIconText: 'text-hue-indigo-deep',
    priority: 'normal',
  },
} as const satisfies Record<string, NotificationTypeRegistryEntry>;

/** Derived union of all notification type strings */
export type NotificationType = keyof typeof NOTIFICATION_TYPE_REGISTRY;

/** Broadcast (page-scoped) types only: everything except `personal: true` entries. */
export type BroadcastNotificationType = {
  [K in NotificationType]: (typeof NOTIFICATION_TYPE_REGISTRY)[K] extends { personal: true } ? never : K;
}[NotificationType];

/**
 * Get the page ID for a notification type.
 */
export function getPageIdFromNotificationType(type: NotificationType): NotificationPageId {
  const pageId = (NOTIFICATION_TYPE_REGISTRY[type] as { pageId?: NotificationPageId }).pageId;
  if (!pageId) {
    throw new Error(`Notification type "${type}" has no page (personal types are not page-scoped)`);
  }
  return pageId;
}

/**
 * Whether a type is registered as personal (`personal: true`).
 * Unknown types return false.
 */
export function isPersonalNotificationType(type: string): boolean {
  if (!Object.prototype.hasOwnProperty.call(NOTIFICATION_TYPE_REGISTRY, type)) return false;
  return (NOTIFICATION_TYPE_REGISTRY[type as NotificationType] as { personal?: boolean }).personal === true;
}
